import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Divider, Avatar, Chip
} from '@mui/material';
import {
  Dashboard, Business, Inventory, ShoppingCart, People,
  CheckCircle, Warning, Analytics, AccountBalance, Menu as MenuIcon,
  Logout, Home, Settings as SettingsIcon, Build, HelpOutline, Person
} from '@mui/icons-material';
import LanguageSwitcher from '../LanguageSwitcher';

const drawerWidth = 260;

const menuItems = [
  { text: 'admin_dashboard', icon: <Dashboard />, path: '/admin' },
  { text: 'manage_suppliers', icon: <Business />, path: '/admin/suppliers' },
  { text: 'manage_products', icon: <Inventory />, path: '/admin/products' },
  { text: 'manage_orders', icon: <ShoppingCart />, path: '/admin/orders' },
  { text: 'manage_users', icon: <People />, path: '/admin/users' },
  { text: 'Customization Requests', icon: <Build />, path: '/admin/customization-requests' },
  { text: 'Product Requests', icon: <HelpOutline />, path: '/admin/product-requests' },
  { text: 'content_approval', icon: <CheckCircle />, path: '/admin/content-approval' },
  { text: 'violations', icon: <Warning />, path: '/admin/violations' },
  { text: 'analytics', icon: <Analytics />, path: '/admin/analytics' },
  { text: 'Escrow', icon: <AccountBalance />, path: '/admin/escrow' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  { text: 'Profile', icon: <Person />, path: '/admin/profile' },
];

const AdminLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="primary">OSESG</Typography>
        <Chip label="Admin Panel" color="error" size="small" sx={{ mt: 0.5 }} />
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button key={item.path} component={Link} to={item.path}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2, mb: 0.5,
              '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
              '&:hover': { bgcolor: 'primary.light', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={t(item.text)} primaryTypographyProps={{ fontSize: '0.9rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        <ListItem button onClick={() => navigate('/')}>
          <ListItemIcon sx={{ minWidth: 40 }}><Home /></ListItemIcon>
          <ListItemText primary="Main Site" />
        </ListItem>
        <ListItem button onClick={() => { dispatch(logout()); navigate('/login'); }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary={t('logout')} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    // Force LTR for the dashboard chrome — see SupplierLayout.js for why.
    <Box sx={{ display: 'flex', direction: 'ltr' }}>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>{t('admin_dashboard')}</Typography>
          <LanguageSwitcher color="inherit" />
          <Avatar sx={{ bgcolor: '#d32f2f', width: 36, height: 36 }}>A</Avatar>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, mt: 8, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
